import type { PayloadAction } from '@reduxjs/toolkit'
import { combineReducers, createSlice, type UnknownAction } from '@reduxjs/toolkit'

/* Zoom Level Slice */
export const zoomLevels = {
  regular: 'regular',
  large: 'large',
  extraLarge: 'extra-large',
} as const

type ZoomLevelKey = keyof typeof zoomLevels
export type ZoomLevel = (typeof zoomLevels)[ZoomLevelKey]

export const zoomLevelValues: Record<ZoomLevel, number> = {
  regular: 1,
  large: 1.2,
  'extra-large': 1.5,
}

export interface ZoomState {
  zoom: ZoomLevel
}

const initialZoomState: ZoomState = {
  zoom: 'regular',
}

const zoomSlice = createSlice({
  name: 'zoom',
  initialState: initialZoomState,
  reducers: {
    increaseZoom: (state) => {
      let newZoom: ZoomLevel

      switch (state.zoom) {
        case zoomLevels.regular:
          newZoom = zoomLevels.large
          break
        case zoomLevels.large:
        case zoomLevels.extraLarge:
          newZoom = zoomLevels.extraLarge
      }
      state.zoom = newZoom
    },
    decreaseZoom: (state) => {
      let newZoom: ZoomLevel
      switch (state.zoom) {
        case zoomLevels.regular:
        case zoomLevels.large:
          newZoom = zoomLevels.regular
          break
        case zoomLevels.extraLarge:
          newZoom = zoomLevels.large
          break
      }

      state.zoom = newZoom
    },
    setZoom: (state, action: PayloadAction<ZoomLevel>) => {
      state.zoom = action.payload
    },
  },
})

/* View Width Slice */
export const viewWidths = {
  slim: 'slim',
  regular: 'regular',
  wide: 'wide',
  ultraWide: 'ultra-wide',
} as const
type ViewWidthKeys = keyof typeof viewWidths
export type ViewWidth = (typeof viewWidths)[ViewWidthKeys]

export interface ViewWidthState {
  width: ViewWidth
}

const initialWidthState: ViewWidthState = {
  width: viewWidths.wide,
}

const widthSlice = createSlice({
  name: 'view-width',
  initialState: initialWidthState,
  reducers: {
    increaseWidth: (state) => {
      let newWidth: ViewWidth
      switch (state.width) {
        case viewWidths.slim:
          newWidth = viewWidths.regular
          break
        case viewWidths.regular:
          newWidth = viewWidths.wide
          break
        case viewWidths.wide:
        case viewWidths.ultraWide:
          newWidth = viewWidths.ultraWide
          break
      }
      state.width = newWidth
    },
    decreaseWidth: (state) => {
      let newWidth: ViewWidth
      switch (state.width) {
        case viewWidths.slim:
        case viewWidths.regular:
          newWidth = viewWidths.slim
          break
        case viewWidths.wide:
          newWidth = viewWidths.regular
          break
        case viewWidths.ultraWide:
          newWidth = viewWidths.wide
          break
      }
      state.width = newWidth
    },
    setWidth: (state, action: PayloadAction<ViewWidth>) => {
      state.width = action.payload
    },
  },
})

export type Collapse = 'collapsed' | 'visible'

export interface CollapseState {
  left?: Collapse
  right?: Collapse
}

const initialCollapseState: CollapseState = {
  left: 'visible',
  right: 'visible',
}

export const collapseSlice = createSlice({
  name: 'panel-collapse',
  initialState: initialCollapseState,
  reducers: {
    setPanelCollapse: (state, { payload }: PayloadAction<{ panel: 'left' | 'right'; collapse: Collapse }>) => {
      state[payload.panel] = payload.collapse
    },
  },
})

export const layoutConfigurationSlice = combineReducers({
  zoom: zoomSlice.reducer,
  width: widthSlice.reducer,
  panels: collapseSlice.reducer,
})

const combinedActions = {
  ...zoomSlice.actions,
  ...widthSlice.actions,
  ...collapseSlice.actions,
}

const allActionsSet = new Set<string>(Object.values(combinedActions).map((action) => action.type))

export function isLayoutConfigAction(action: UnknownAction): boolean {
  return allActionsSet.has(action.type)
}

export const { decreaseWidth, increaseWidth, setWidth, decreaseZoom, increaseZoom, setZoom, setPanelCollapse } = combinedActions
export const layoutConfigurationActions = combinedActions
